import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { AssignmentWindowConfigStore } from '@/stores/AssignmentWindowConfigStore';
import type { AssignmentWindowConfig } from '@/utils/evaluatePreAssignment';
import type { IAssignmentWindowConfigInfo } from '~/api/IAssignmentWindowConfigInfo';
import type { IAssignmentWindowConfigRespMsg } from '~/api/messages/IAssignmentWindowConfigRespMsg';

/**
 * Wires the BE's assignment window configuration into `AssignmentWindowConfigStore`.
 *
 * Data flow:
 *  1. BE sends `AssignmentWindowConfigRespMsg` automatically on user connect,
 *     reconnect, and reload — no explicit request needed.
 *  2. Admin edits in the config UI trigger a broadcast that carries ONLY the
 *     record(s) that changed (agency default OR a single vehicle-type override).
 *
 * Because the broadcast is a delta, records are merged by `id` (ZD-20171): updating
 * one scope must not wipe out the other. Records with `status: '0'` are treated as
 * soft-deletes at ingest — the store holds active records only, so any existing
 * entry with a matching id is removed and inactive rows are never persisted.
 */
@SignalrListener
@service(AssignmentWindowManager)
export class AssignmentWindowManager {
  @inject(Logger) protected logger: Logger;

  @inject(AssignmentWindowConfigStore) protected store: AssignmentWindowConfigStore;

  get config(): AssignmentWindowConfig {
    return this.buildConfig();
  }

  @SignalrMessage('AssignmentWindowConfigRespMsg')
  onAssignmentWindowConfigRespMsg(response: IAssignmentWindowConfigRespMsg): void {
    this.updateConfigs(response?.assignmentWindowConfigs);
  }

  updateConfigs(configs?: IAssignmentWindowConfigInfo[]): void {
    if (!configs) return;

    // Empty list = no configuration exists at all (e.g. fresh agency, or the last
    // record was just deleted). Reset rather than ignore, so the FE reflects the
    // "feature disabled" state instead of holding stale entries.
    if (!configs.length) {
      this.store.set([]);
      return;
    }

    this.warnOnInvalidPayloads(configs);

    const active: IAssignmentWindowConfigInfo[] = [];
    configs.forEach((c) => {
      if (c.status === STATUS.INACTIVE) {
        // Drop soft-deleted rows on ingest. `remove` is a no-op when the id is
        // absent (BE connect snapshot), and evicts the stale active row when a
        // delta message flips an existing entry to inactive.
        this.store.remove(c.id);
      }
      else {
        active.push(c);
      }
    });

    if (active.length) {
      this.store.batchUpdate(active);
    }
  }

  private buildConfig(): AssignmentWindowConfig {
    const all = this.store.all();

    const agencyDefault = all.find((c) => !c.vehicleTypeId);
    const overrides = all
      .filter((c): c is IAssignmentWindowConfigInfo & { vehicleTypeId: string } => !!c.vehicleTypeId)
      .map((c) => ({
        vehicleTypeId: c.vehicleTypeId,
        windowMinutes: AssignmentWindowManager.toMinutes(c.windowMinutes),
      }))
      .filter((o): o is { vehicleTypeId: string; windowMinutes: number } => o.windowMinutes !== null);

    return {
      agencyWindowMinutes: agencyDefault
        ? AssignmentWindowManager.toMinutes(agencyDefault.windowMinutes)
        : null,
      overrides,
    };
  }

  private warnOnInvalidPayloads(configs: IAssignmentWindowConfigInfo[]): void {
    configs.forEach((c) => {
      if (c.status === STATUS.INACTIVE) return;
      if (AssignmentWindowManager.isBlank(c.windowMinutes)) return;
      if (AssignmentWindowManager.toMinutes(c.windowMinutes) !== null) return;

      if (c.vehicleTypeId) {
        this.logger.warn?.(
          `[AssignmentWindow] Ignoring override for vehicleTypeId=${c.vehicleTypeId} with unparseable windowMinutes: ${JSON.stringify(c.windowMinutes)}`,
        );
      }
      else {
        this.logger.warn?.(
          `[AssignmentWindow] Ignoring agency default with unparseable windowMinutes: ${JSON.stringify(c.windowMinutes)}`,
        );
      }
    });
  }

  /**
   * Parses the BE's string-encoded minute value. Returns null for missing or
   * non-numeric input. A configured '0' is preserved as 0 (feature explicitly
   * off for that scope) rather than being collapsed to null by `||`.
   */
  private static toMinutes(value: string | null | undefined): number | null {
    if (AssignmentWindowManager.isBlank(value)) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private static isBlank(value: string | null | undefined): boolean {
    return value === null || value === undefined || `${value}`.trim() === '';
  }
}
